import { useCallback, useEffect, useRef, useState } from 'react';

export interface GalleryImage {
  id: string;
  url: string;
}

// Vollbild-Ansicht mit Durchklicken (Pfeile, Tastatur) und Wischen (Touch).
// Das Bild skaliert mit dem Viewport (max 100vw/100vh abzüglich Rand).
export function ImageLightbox({ images, startIndex = 0, onClose, alt = '' }: { images: GalleryImage[]; startIndex?: number; onClose: () => void; alt?: string }) {
  const [index, setIndex] = useState(Math.min(startIndex, images.length - 1));
  const touchX = useRef<number | null>(null);
  const count = images.length;

  const go = useCallback((delta: number) => setIndex((i) => (i + delta + count) % count), [count]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft' && count > 1) go(-1);
      else if (e.key === 'ArrowRight' && count > 1) go(1);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [go, onClose, count]);

  const navBtn: React.CSSProperties = {
    position: 'absolute', top: '50%', transform: 'translateY(-50%)', fontSize: '1.6rem', width: 44, height: 44,
    borderRadius: '50%', background: 'rgba(0,0,0,0.5)', color: '#fff', border: 'none', cursor: 'pointer',
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Bildansicht"
      onClick={onClose}
      onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => {
        if (touchX.current == null || count < 2) return;
        const dx = e.changedTouches[0].clientX - touchX.current;
        touchX.current = null;
        if (Math.abs(dx) > 50) go(dx < 0 ? 1 : -1);
      }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 16 }}
    >
      <img
        src={images[index].url}
        alt={alt}
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 'min(100%, 1200px)', maxHeight: '100%', objectFit: 'contain', borderRadius: 8 }}
      />
      <button type="button" aria-label="Schließen" onClick={onClose} style={{ ...navBtn, top: 16, right: 16, transform: 'none' }}>
        ×
      </button>
      {count > 1 && (
        <>
          <button type="button" aria-label="Vorheriges Bild" onClick={(e) => { e.stopPropagation(); go(-1); }} style={{ ...navBtn, left: 8 }}>
            ‹
          </button>
          <button type="button" aria-label="Nächstes Bild" onClick={(e) => { e.stopPropagation(); go(1); }} style={{ ...navBtn, right: 8 }}>
            ›
          </button>
          <span style={{ position: 'absolute', bottom: 16, left: 0, right: 0, textAlign: 'center', color: '#fff' }} aria-live="polite">
            {index + 1} / {count}
          </span>
        </>
      )}
    </div>
  );
}

// Kleines Vorschaubild; Klick öffnet die Galerie ab diesem Bild.
export function ImageThumbs({ images, size = 72, onlyFirst = false, alt = '' }: { images: GalleryImage[]; size?: number; onlyFirst?: boolean; alt?: string }) {
  const [open, setOpen] = useState<number | null>(null);
  const close = useCallback(() => setOpen(null), []);
  if (images.length === 0) return null;
  const shown = onlyFirst ? images.slice(0, 1) : images;
  return (
    <>
      {shown.map((img, i) => (
        <button
          key={img.id}
          type="button"
          aria-label={images.length > 1 && onlyFirst ? `Produktbilder ansehen (${images.length})` : 'Bild vergrößern'}
          onClick={() => setOpen(i)}
          style={{ padding: 0, border: 'none', background: 'none', cursor: 'zoom-in', flexShrink: 0 }}
        >
          <img src={img.url} alt={alt} loading="lazy" style={{ width: size, height: size, objectFit: 'cover', borderRadius: 8, display: 'block' }} />
        </button>
      ))}
      {open !== null && <ImageLightbox images={images} startIndex={open} onClose={close} alt={alt} />}
    </>
  );
}
